import CartSummary from '@/src/common/components/elements/Checkout/CartSummary'
import CheckoutHeader from '@/src/common/components/elements/Checkout/CheckoutHeader'
import { LoadingOverlay } from '@/src/modules/checkout/forms/StyledComponents'
import { TCartSummaryProps } from '@/src/modules/checkout/utilities/types'
import { ClipLoader } from 'react-spinners'
import {
    BackButton,
    Container,
} from '@/src/modules/checkout/ShippingStep/ShippingStepStyles'
import {
    FullWidthWrapper,
    HalfContentContainer,
    LeftContentWrapper,
    RightContentWrapper,
} from '@/src/common/components/elements/StyledComponents'
import { useRouter } from 'next/router'
import { theme } from '@/src/stitches.config'
import Link from 'next/link'
import { ReactNode } from 'react'
import { TPartnerPayload } from '@/src/modules/partners/types'
import determineBuyPage from '@/src/modules/partners/utilities/determineBuyPage'

type TBaseProps = {
    currentStep: number
    hasPlastic: boolean
    cartSummary: TCartSummaryProps
    children?: JSX.Element | JSX.Element[]
    showPageLoader?: boolean
    backUrl?: string
    backText?: string
}

type TBaseContentProps = {
    showPageLoader: boolean
    currentStep: number
    hasPlastic: boolean
    children: ReactNode
    partnerData?: TPartnerPayload
}

// base loader and header
const BaseCheckoutContent = ({
    showPageLoader,
    currentStep,
    hasPlastic,
    children,
    partnerData,
}: TBaseContentProps) => {
    return (
        <Container>
            {showPageLoader && (
                <LoadingOverlay>
                    <ClipLoader color={'black'} loading={true} size={30} />
                </LoadingOverlay>
            )}
            <CheckoutHeader
                currentStep={currentStep}
                hasPlastic={hasPlastic}
                partnerData={partnerData}
            />
            {children}
        </Container>
    )
}

const CheckoutBaseFormat = ({
    currentStep,
    hasPlastic,
    children,
    cartSummary,
    showPageLoader = false,
    backUrl = '/',
    backText = 'Back',
}: TBaseProps) => {
    const router = useRouter()
    const {
        cartData,
        removeItemFromCart,
        showPromoCodeInput,
        updatePromoCode,
        partnerData,
    } = cartSummary
    // if in partner flow, set to step 2 (payment)
    const adjustedCurrentStep: number = partnerData ? 2 : currentStep
    const continueShoppingUrl = determineBuyPage('/', partnerData)

    const hasItems: boolean =
        (cartData?.cart_contents?.deliveries?.length ?? 0) > 0

    if (!hasItems) {
        return (
            <BaseCheckoutContent
                showPageLoader={showPageLoader}
                currentStep={adjustedCurrentStep}
                hasPlastic={hasPlastic}
                partnerData={partnerData}
            >
                <FullWidthWrapper
                    flexDirection={'column'}
                    background={'lightest'}
                >
                    {children}
                </FullWidthWrapper>
            </BaseCheckoutContent>
        )
    }

    return (
        <BaseCheckoutContent
            showPageLoader={showPageLoader}
            currentStep={adjustedCurrentStep}
            hasPlastic={hasPlastic}
            partnerData={partnerData}
        >
            <FullWidthWrapper flexDirection={'column'}>
                <HalfContentContainer background={'lightest'}>
                    <LeftContentWrapper>
                        {hasPlastic ? (
                            <BackButton
                                onClick={() => router.push(backUrl)}
                                backText={backText}
                            />
                        ) : (
                            <Link
                                href={continueShoppingUrl}
                                style={{
                                    color: `${theme.colors.primary}`,
                                    textDecoration: 'underline',
                                }}
                            >
                                Continue Shopping
                            </Link>
                        )}
                        <>{children}</>
                    </LeftContentWrapper>
                </HalfContentContainer>
                <HalfContentContainer>
                    <RightContentWrapper>
                        <CartSummary
                            cartData={cartData}
                            removeItemFromCart={removeItemFromCart}
                            updatePromoCode={updatePromoCode}
                            showPromoCodeInput={
                                showPromoCodeInput && !partnerData
                            }
                            partnerData={partnerData}
                        />
                    </RightContentWrapper>
                </HalfContentContainer>
            </FullWidthWrapper>
        </BaseCheckoutContent>
    )
}

export default CheckoutBaseFormat
